import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, forwardRef, HostBinding, Input, Optional, Self } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NgControl, Validators } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { Offer, OfferFilters } from 'src/app/commons/models/offer.model';
import * as OfferActions from 'src/app/store/actions/offer.actions';
import * as OfferSelectors from 'src/app/store/selectors/offer.selectors';
import { AppState } from 'src/app/store/reducers';


export const CONTACT_FIELD_MAT_FORM_FIELD_CONTROL: any = {
  provide: MatFormFieldControl,
  useExisting: forwardRef(() => OfferFieldComponent),
};

@Component({
  selector: "app-offer-field",
  templateUrl: "./offer-field.component.html",
  styleUrls: ["./offer-field.component.scss"],
  providers: [CONTACT_FIELD_MAT_FORM_FIELD_CONTROL],
  host: {
    "[class.floating]": "shouldLabelFloat",
    "[id]": "id",
  },
})
export class OfferFieldComponent
  implements ControlValueAccessor, MatFormFieldControl<Offer> {
  private unsubscribe$ = new Subject<void>();
  private _value: Offer;
  private _disabled = false;

  private _required = false;

  get errorState(): boolean {
    return !this.ngControl.valid;
  }
  focused: boolean; //TODO
  controlType?: string = "offer-selection-field";
  autofilled?: boolean;
  offerAriaDescribedBy?: string;
  currentFormControlName: string;


  @Input()
  offerFilters: OfferFilters;

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    @Optional() @Self() public ngControl: NgControl
  ) {

    this.actions$
      .pipe(
        ofType(OfferActions.offerSelected),
        takeUntil(this.unsubscribe$),
        withLatestFrom(this.store$.select(OfferSelectors.getCurrentFormControlName)),
        filter(([_, currentFormControlName]) => currentFormControlName == this.currentFormControlName),
        map(([{ offer }, _]) => (offer ? new Offer(offer) : null))

      )
      .subscribe((offer) => {
        this.value = offer;
        this.notifyValueChange();
      });
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }
  stateChanges = new Subject<void>();
  id: string;

  ngAfterContentInit(): void {
    const control = this.ngControl && this.ngControl.control;
    if (control) {
      this.currentFormControlName = `${this.getControlName(control)}_${Math.random()}`;
    }
 }

 private getControlName(c: AbstractControl): string | null {
  const formGroup = c.parent.controls;
  return Object.keys(formGroup).find(name => c === formGroup[name]) || null;
}


  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(plh) {
    this._placeholder = plh;
    this.stateChanges.next();
  }
  private _placeholder: string;

  get empty() {
    return !this.value;
  }

  @HostBinding("class.floating")
  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  get required() {
    return this._required || this.ngControl?.control?.hasValidator(Validators.required) || false;
  }

  @Input()
  set required(req) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }

  get disabled(): boolean {
    return this._disabled;
  }

  @Input()
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  setDescribedByIds(ids: string[]): void {
    //TODO
  }
  onContainerClick(event: MouseEvent): void {
    if (!this.disabled) {
      if ((event.target as Element).tagName.toLowerCase() != "mat-icon") {
        this.selectOffer();
      }
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.stateChanges.complete();
  }

  selectOffer() {
    this.store$.dispatch(OfferActions.selectOffer({ filters: this.offerFilters, currentFormControlName: this.currentFormControlName }));
  }

  clearOffer() {
    this.value = null;
  }

  set value(value: Offer) {
    this._value = value;
    this.notifyValueChange();
    this.stateChanges.next();
  }

  get value(): Offer {
    return this._value;
  }

  onChange: (value) => {};
  onTouched: () => {};

  notifyValueChange(): void {
    if (this.onChange) {
      this.onChange(this.value);
    }
  }

  writeValue(obj: Offer): void {
    this._value = obj;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
